import { isNil } from 'lodash';
import { formatCurrency } from './FormatUtilities';
import { SameFields } from './TypeUtilities';

// Codes must be at least this long
export const MIN_CODE_BASE_LENGTH = 5;

export const MIN_EXPLORE_PAGE_PERCENT_DISCOUNT_AS_DECIMAL = 0.05;
const MAXIMUM_PROMO_VALUE_PERCENT_AS_DECIMAL = 0.5;

export function calculateMinimumMinOrderValue(promoCodeValue: number, shefPlatformPercent: number): number {
  // If a shef gets 75% of the subtotal, and a promo code is for $15, minimum order amount needs to be $20
  return Math.ceil(promoCodeValue / Math.min(1 - shefPlatformPercent / 100, MAXIMUM_PROMO_VALUE_PERCENT_AS_DECIMAL));
}

export function calculateMaxPromoValue(isPercentage: boolean, minOrderValue: number | null | undefined): number {
  if (isPercentage) {
    return MAXIMUM_PROMO_VALUE_PERCENT_AS_DECIMAL * 100;
  }

  if (minOrderValue === null || minOrderValue === undefined) {
    throw new Error('need a minOrderValue if calulating based on non-percentage');
  }

  return Math.floor(minOrderValue * MAXIMUM_PROMO_VALUE_PERCENT_AS_DECIMAL);
}

export enum CurrencyUnit {
  MINOR,
  MAJOR,
}

export interface IShefPromoCodeForm {
  unit: CurrencyUnit;
  codeBase: string;
  value: number;
  isPercentage: boolean;
  isExplorePagePromo: boolean;

  firstPromoOnly?: boolean;
  minOrderValue?: number;
  startDate?: string;
  endDate?: string;
  maxUsesPerUser?: number;
  maxUses?: number;
}

export const getErrors =
  (shefPlatformPercent: number | null) =>
  (input: Partial<IShefPromoCodeForm>): SameFields<IShefPromoCodeForm, string> => {
    const errors: SameFields<IShefPromoCodeForm, string> = {};

    if (input.endDate) {
      if (new Date(input.endDate).getTime() < Date.now()) {
        errors.endDate = 'The end date must be in the future.';
      }

      if (input.startDate && new Date(input.startDate).getTime() > new Date(input.endDate).getTime()) {
        errors.startDate = 'The start date must be before the end date.';
      }
    }

    if (input.maxUsesPerUser && input.maxUsesPerUser <= 0) {
      errors.maxUsesPerUser = 'Max uses per user must be greater than zero.';
    }

    if (input.maxUses && input.maxUses <= 0) {
      errors.maxUses = 'Max uses must be greater than zero.';
    }

    if (!input.value || input.value <= 0) {
      errors.value = 'Value must be greater than zero.';
    }

    if (!input.codeBase || input.codeBase.length < MIN_CODE_BASE_LENGTH) {
      errors.codeBase = `Promo code must be at least ${MIN_CODE_BASE_LENGTH} characters long.`;
    }

    if (input.value === undefined) {
      errors.value = 'Please provide a value.';
      return errors;
    }

    if (input.isExplorePagePromo && input.minOrderValue) {
      errors.isExplorePagePromo = `To be displayed on the home page, promos must not have a minimum order value.`;
    }

    const unitMultiplier = input.unit === CurrencyUnit.MAJOR ? 100 : 1;
    const isPercentage = Boolean(input.isPercentage);
    const maxValue = calculateMaxPromoValue(isPercentage, input.minOrderValue ?? 0);
    if (isPercentage) {
      if (input.value > maxValue) {
        errors.value = `Value must be less than ${Math.floor(MAXIMUM_PROMO_VALUE_PERCENT_AS_DECIMAL * 100)}%`;
      } else if (input.isExplorePagePromo && input.value < MIN_EXPLORE_PAGE_PERCENT_DISCOUNT_AS_DECIMAL) {
        errors.value = `To be displayed on the home page, promos must have a value of at least ${Math.floor(
          MIN_EXPLORE_PAGE_PERCENT_DISCOUNT_AS_DECIMAL * 100
        )}%.`;
      }
    } else {
      if (isNil(input.minOrderValue)) {
        const shefPlatformPercentToValidateMinOrderValue = shefPlatformPercent ?? 0;

        errors.minOrderValue = `You must provide a minimum order value of at least ${formatCurrency(
          calculateMinimumMinOrderValue(input.value, shefPlatformPercentToValidateMinOrderValue) * unitMultiplier
        )} to make a promo code worth ${formatCurrency(input.value * unitMultiplier)}`;
        return errors;
      }

      if (input.value > maxValue) {
        errors.value = `The maximum value for a promo code with a ${formatCurrency(
          input.minOrderValue * unitMultiplier
        )} minimum is ${formatCurrency(maxValue * unitMultiplier)}`;
      }
    }

    return errors;
  };
